import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/adminAuth';
import { toast } from 'sonner';
import {
  FileText, Download, RefreshCw, ChevronDown, ChevronUp,
  Receipt, Building2, MapPin, Percent
} from 'lucide-react';
import { HeaderSkeleton, TableSkeleton } from './skeletons/Skeletons';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// GSTR-1 Component
function GSTR1Summary({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('b2b');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await adminApi.get(`/api/admin/accounting/gst/gstr1-summary?period=${period}`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load GSTR-1 data');
      } finally {
        setLoading(false);
      }
    };
    if (period) fetchData();
  }, [period]);

  const downloadJSON = async () => {
    try {
      const res = await adminApi.get(`/api/admin/accounting/export/gstr1-json?period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GSTR1_${period.replace('-', '')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('GSTR-1 JSON downloaded');
    } catch (err) {
      toast.error('Failed to download JSON');
    }
  };

  if (loading) {
    return <div data-testid="gstr1-loading" className="space-y-4"><HeaderSkeleton withActions={false} /><TableSkeleton rows={6} columns={4} /></div>;
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 rounded-xl">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold">GSTR-1 Summary</h2>
            <p className="text-blue-100 text-sm mt-1">{data.period_name}</p>
            <p className="text-blue-200 text-xs mt-1">GSTIN: {data.gstin}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={downloadJSON}>
            <Download className="w-4 h-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-white/40">
          <p className="text-xs text-neutral-500">Taxable Value</p>
          <p className="text-xl font-bold text-[#1a1a2e]">{formatCurrency(data.totals?.taxable_value)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-white/40">
          <p className="text-xs text-neutral-500">CGST + SGST</p>
          <p className="text-xl font-bold text-blue-600">
            {formatCurrency((data.totals?.cgst || 0) + (data.totals?.sgst || 0))}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-white/40">
          <p className="text-xs text-neutral-500">IGST</p>
          <p className="text-xl font-bold text-purple-600">{formatCurrency(data.totals?.igst)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-white/40">
          <p className="text-xs text-neutral-500">Invoice Value</p>
          <p className="text-xl font-bold text-emerald-600">{formatCurrency(data.totals?.invoice_value)}</p>
        </div>
      </div>

      {/* B2B Section */}
      <div className="soft-card overflow-hidden">
        <button 
          className="w-full px-5 py-4 flex items-center justify-between bg-white/40 hover:bg-neutral-100"
          onClick={() => setExpandedSection(expandedSection === 'b2b' ? null : 'b2b')}
        >
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-semibold text-[#1a1a2e]">B2B Invoices (Registered)</p>
              <p className="text-xs text-neutral-500">{data.b2b_summary?.count || 0} invoices</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-bold text-blue-600">{formatCurrency(data.b2b_summary?.taxable)}</span>
            {expandedSection === 'b2b' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        {expandedSection === 'b2b' && (
          <div className="p-4 max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/40 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Invoice</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">GSTIN</th>
                  <th className="px-3 py-2 text-right">Taxable</th>
                  <th className="px-3 py-2 text-right">GST</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {data.b2b_invoices?.map((inv, i) => (
                  <tr key={i} className="hover:bg-white/60">
                    <td className="px-3 py-2 font-medium">{inv.invoice_number}</td>
                    <td className="px-3 py-2 text-neutral-600">{inv.client_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-500">{inv.gstin}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(inv.taxable_value)}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(inv.total_gst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* B2C Section */}
      <div className="soft-card overflow-hidden">
        <button 
          className="w-full px-5 py-4 flex items-center justify-between bg-white/40 hover:bg-neutral-100"
          onClick={() => setExpandedSection(expandedSection === 'b2c' ? null : 'b2c')}
        >
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-amber-600" />
            <div className="text-left">
              <p className="font-semibold text-[#1a1a2e]">B2C Invoices (Unregistered)</p>
              <p className="text-xs text-neutral-500">{data.b2c_summary?.count || 0} invoices</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-bold text-amber-600">{formatCurrency(data.b2c_summary?.taxable)}</span>
            {expandedSection === 'b2c' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        {expandedSection === 'b2c' && (
          <div className="p-4 max-h-60 overflow-y-auto">
            {data.b2c_invoices?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-white/40">
                  <tr>
                    <th className="px-3 py-2 text-left">Invoice</th>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-right">Taxable</th>
                    <th className="px-3 py-2 text-right">GST</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.b2c_invoices?.map((inv, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{inv.invoice_number}</td>
                      <td className="px-3 py-2">{inv.client_name}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(inv.taxable_value)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(inv.total_gst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-center text-neutral-500 py-4">No B2C invoices</p>
            )}
          </div>
        )}
      </div>

      {/* HSN Summary */}
      <div className="soft-card overflow-hidden">
        <button 
          className="w-full px-5 py-4 flex items-center justify-between bg-white/40 hover:bg-neutral-100"
          onClick={() => setExpandedSection(expandedSection === 'hsn' ? null : 'hsn')}
        >
          <div className="flex items-center gap-3">
            <Percent className="w-5 h-5 text-teal-600" />
            <div className="text-left">
              <p className="font-semibold text-[#1a1a2e]">HSN Summary</p>
              <p className="text-xs text-neutral-500">{data.hsn_summary?.length || 0} HSN codes</p>
            </div>
          </div>
          {expandedSection === 'hsn' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {expandedSection === 'hsn' && (
          <div className="p-4">
            <table className="w-full text-sm">
              <thead className="bg-white/40">
                <tr>
                  <th className="px-3 py-2 text-left">HSN Code</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-right">Taxable</th>
                  <th className="px-3 py-2 text-right">GST</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {data.hsn_summary?.map((hsn, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-mono">{hsn.hsn_code}</td>
                    <td className="px-3 py-2">{hsn.description}</td>
                    <td className="px-3 py-2 text-center">{hsn.quantity}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(hsn.taxable_value)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(hsn.total_gst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// GSTR-3B Component
function GSTR3BSummary({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await adminApi.get(`/api/admin/accounting/gst/gstr3b-summary?period=${period}`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load GSTR-3B data');
      } finally {
        setLoading(false);
      }
    };
    if (period) fetchData();
  }, [period]);

  if (loading) {
    return <div data-testid="gstr3b-loading" className="space-y-4"><HeaderSkeleton withActions={false} /><TableSkeleton rows={6} columns={4} /></div>;
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-5 rounded-xl">
        <h2 className="text-xl font-bold">GSTR-3B Summary</h2>
        <p className="text-purple-100 text-sm mt-1">{data.period_name}</p>
        <p className="text-purple-200 text-xs mt-1">GSTIN: {data.gstin}</p>
      </div>

      {/* Table 3.1 - Outward Supplies */}
      <div className="glass-card p-5">
        <h3 className="font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-sm font-bold">3.1</span>
          Outward Taxable Supplies
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-xl">
            <p className="text-sm text-blue-600 font-medium">Intra-State Supplies</p>
            <p className="text-xl font-bold text-blue-800">{formatCurrency(data.table_3_1?.outward_taxable?.intra_state?.taxable)}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <span>CGST: {formatCurrency(data.table_3_1?.outward_taxable?.intra_state?.cgst)}</span>
              <span>SGST: {formatCurrency(data.table_3_1?.outward_taxable?.intra_state?.sgst)}</span>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl">
            <p className="text-sm text-purple-600 font-medium">Inter-State Supplies</p>
            <p className="text-xl font-bold text-purple-800">{formatCurrency(data.table_3_1?.outward_taxable?.inter_state?.taxable)}</p>
            <div className="mt-2 text-sm">
              <span>IGST: {formatCurrency(data.table_3_1?.outward_taxable?.inter_state?.igst)}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-neutral-100 rounded-xl flex justify-between">
          <span className="font-medium">Total Tax Liability</span>
          <span className="font-bold text-[#1a1a2e]">{formatCurrency(data.table_3_1?.total_tax)}</span>
        </div>
      </div>

      {/* Table 4 - ITC */}
      <div className="glass-card p-5">
        <h3 className="font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded flex items-center justify-center text-sm font-bold">4</span>
          Eligible Input Tax Credit (ITC)
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-white/40 rounded-xl">
            <p className="text-xs text-neutral-500">CGST</p>
            <p className="text-lg font-bold text-[#1a1a2e]">{formatCurrency(data.table_4?.itc_available?.cgst)}</p>
          </div>
          <div className="text-center p-3 bg-white/40 rounded-xl">
            <p className="text-xs text-neutral-500">SGST</p>
            <p className="text-lg font-bold text-[#1a1a2e]">{formatCurrency(data.table_4?.itc_available?.sgst)}</p>
          </div>
          <div className="text-center p-3 bg-white/40 rounded-xl">
            <p className="text-xs text-neutral-500">IGST</p>
            <p className="text-lg font-bold text-[#1a1a2e]">{formatCurrency(data.table_4?.itc_available?.igst)}</p>
          </div>
        </div>
      </div>

      {/* Table 6 - Payment */}
      <div className="glass-card p-5">
        <h3 className="font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-amber-100 text-amber-600 rounded flex items-center justify-center text-sm font-bold">6</span>
          Payment of Tax
        </h3>
        <table className="w-full text-sm">
          <thead className="bg-white/40">
            <tr>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">CGST</th>
              <th className="px-4 py-2 text-right">SGST</th>
              <th className="px-4 py-2 text-right">IGST</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/30">
            <tr>
              <td className="px-4 py-3">Tax Payable</td>
              <td className="px-4 py-3 text-right">{formatCurrency(data.table_6?.tax_payable?.cgst)}</td>
              <td className="px-4 py-3 text-right">{formatCurrency(data.table_6?.tax_payable?.sgst)}</td>
              <td className="px-4 py-3 text-right">{formatCurrency(data.table_6?.tax_payable?.igst)}</td>
            </tr>
            <tr className="text-emerald-600">
              <td className="px-4 py-3">Less: ITC Utilized</td>
              <td className="px-4 py-3 text-right">-{formatCurrency(data.table_6?.itc_utilized?.cgst)}</td>
              <td className="px-4 py-3 text-right">-{formatCurrency(data.table_6?.itc_utilized?.sgst)}</td>
              <td className="px-4 py-3 text-right">-{formatCurrency(data.table_6?.itc_utilized?.igst)}</td>
            </tr>
            <tr className="text-amber-600">
              <td className="px-4 py-3">Less: TDS/TCS Credit</td>
              <td className="px-4 py-3 text-right" colSpan={3}>-{formatCurrency(data.table_6?.tds_tcs_credit)}</td>
            </tr>
            <tr className="font-bold bg-red-50">
              <td className="px-4 py-3 text-red-700">Net Cash Payable</td>
              <td className="px-4 py-3 text-right text-red-700">{formatCurrency(data.table_6?.net_cash_payable?.cgst)}</td>
              <td className="px-4 py-3 text-right text-red-700">{formatCurrency(data.table_6?.net_cash_payable?.sgst)}</td>
              <td className="px-4 py-3 text-right text-red-700">{formatCurrency(data.table_6?.net_cash_payable?.igst)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-5 rounded-xl">
        <h3 className="font-semibold mb-4">Payment Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-neutral-400">Total Liability</p>
            <p className="text-2xl font-bold">{formatCurrency(data.summary?.total_liability)}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-400">Total ITC</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.summary?.total_itc)}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-400">Cash Required</p>
            <p className="text-2xl font-bold text-amber-400">{formatCurrency(data.summary?.total_cash_required)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main GST Returns Component
export default function GSTReturns() {
  const [activeReturn, setActiveReturn] = useState('gstr1');
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  return (
    <div className="space-y-6" data-testid="gst-returns">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">GST Returns</h1>
          <p className="text-sm text-neutral-500">GSTR-1 & GSTR-3B summaries for filing</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/40 pb-1">
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeReturn === 'gstr1'
              ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-white/60'
          }`}
          onClick={() => setActiveReturn('gstr1')}
        >
          GSTR-1
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeReturn === 'gstr3b'
              ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-white/60'
          }`}
          onClick={() => setActiveReturn('gstr3b')}
        >
          GSTR-3B
        </button>
      </div>

      {/* Content */}
      {activeReturn === 'gstr1' && <GSTR1Summary period={period} />}
      {activeReturn === 'gstr3b' && <GSTR3BSummary period={period} />}
    </div>
  );
}
